import {FC, useEffect, useMemo} from "react";
import {Stack} from "@mui/material";
import ProgressWidget from "./ProgressWidget";
import {useTeamProgressContext} from "@/contexts/TeamProgressContext";
import {fetchRequest, useApi} from "@buildwitt/shapeshift-identity";
import dayjs from "dayjs";
import {IWidget} from "@/types/widget";
import {TeamProgressSection, TeamProgressWidget} from "@/enums";
import ProgressWidgetSkeleton from "./ProgressWidgetSkeleton";
import { getDateTo } from "../../utils/common-utils";

interface IWidgetResponseDto {
	completedCourses: string;
	completedLessons: string;
	timeInTraining: string;
	activeUsers: string;
	inactiveUsers: string;
	usersWithOverdueLessons: string;
}

interface WidgetListProps {
	selectedTile: string;
	setSelectedTile: (tileKey: string) => void;
}

const WidgetList: FC<WidgetListProps> = ({selectedTile, setSelectedTile}) => {
	const {filters: {archivedUsers, dateFrom, dateTo}, updateSectionState} = useTeamProgressContext();

	const getApiCallConfig = (archivedUsersParam: boolean, dateFromParam: Date, dateToParam: Date) : fetchRequest<any> => ({
		method: "GET",
		url: "/api/v1/team-progress/tiles",
		config: {
			params: {
				archivedUsers: archivedUsersParam,
				dateFrom: dayjs(dateFromParam).format("YYYY-MM-DD"),
				dateTo: getDateTo(dateToParam)
			}
		}
	});
	const [widgetsResponse, getWidgetsData] = useApi<IWidgetResponseDto>();
	const isFetching = !widgetsResponse.statusCode || widgetsResponse.isLoading || !widgetsResponse.data;

	useEffect(() => {
		getWidgetsData(getApiCallConfig(archivedUsers, dateFrom, dateTo));
	}, [dateFrom.toDateString(), dateTo.toDateString(), archivedUsers]);

	useEffect(() => {
		updateSectionState(isFetching, TeamProgressSection.widgets);
	}, [isFetching])

	const widgets = useMemo(() => {
		if (!widgetsResponse.data) return [];

		const result: IWidget[] = [];
		const redFlagWidgets = [TeamProgressWidget.inactiveUsers, TeamProgressWidget.usersWithOverdueLessons];
		const underConstructionWidgets = [];

		for (const [attribute, value] of Object.entries(widgetsResponse.data)){
			const widget: IWidget = {
				key: attribute,
				value: value,
				isRedFlag: redFlagWidgets.includes(attribute as TeamProgressWidget),
				underConstruction: underConstructionWidgets.includes(attribute as TeamProgressWidget)
			};
			result.push(widget);
		}

		return result;

	}, [widgetsResponse.data]);

	const handleUpdateSelectedWidget = (tileKey: string) => setSelectedTile(tileKey);

	return(
		<Stack
			direction="row"
			gap={2}
			sx={{flexWrap: "wrap"}}
		>
			{
				!isFetching &&
				widgets.map(widget => {
					const selected = selectedTile === widget.key;

					return (
						<ProgressWidget
							key={widget.key}
							widget={widget}
							isSelected={selected}
							onSelectWidget={handleUpdateSelectedWidget}
						/>
					);
				})
			}
			{
				isFetching &&
				Array.from({length: 6}, item =>
					<ProgressWidgetSkeleton key={`skeleton-${item}`} />
				)
			}
		</Stack>
	);
}

export default WidgetList;